import { FC, useRef } from 'react';
import { Grid, Typography } from '@material-ui/core';
import clsx from 'clsx';
import { useTranslation } from 'react-i18next';
import Loader from 'react-loader-spinner';
import { useStyles } from './styles';
import Participant from '../Participant';
import userIcon from '../../../../assets/streaming/actions/user-white.svg';
import DisconnectDialog from '../DisconnectDialog';
import useStreamingLogic from '../hooks/useStreamingLogic';
import RaiseHandQueue from '../RaiseHandQueue';
import mainLogoDark from '../../../../assets/logo/main-dark.png';
import Colors from '../../../../assets/Colors';
import VideoOverlay from '../VideoOverlay';
import DominantSpeakerOverlay from '../DominantSpeakerOverlay';

interface DesktopVideoLayoutProps {
    isFullScreen: boolean;
    setIsFullScreen: any;
}

const DesktopVideoLayout: FC<DesktopVideoLayoutProps> = ({ isFullScreen, setIsFullScreen }) => {
    const classes = useStyles();
    const { t } = useTranslation();
    const mainContainerRef = useRef(null);

    const {
        openDisconnectModal,
        disconnectEvent,
        closeEvent,
        setOpenDisconnectModal,
        participants,
        mainParticipant,
        influencerUsername,
        remoteParticipant,
        localUserUsername,
    } = useStreamingLogic();

    const handleFullScreen = () => {
        if (isFullScreen) {
            if (document.exitFullscreen) {
                document.exitFullscreen();
                // @ts-ignore
            } else if (document.webkitExitFullscreen) {
                /* Safari */
                // @ts-ignore
                document.webkitExitFullscreen();
                // @ts-ignore
            } else if (document.msExitFullscreen) {
                /* IE11 */
                // @ts-ignore
                document.msExitFullscreen();
            }
        } else {
            // @ts-ignore
            // eslint-disable-next-line no-lonely-if
            if (mainContainerRef?.current?.requestFullscreen) {
                // @ts-ignore
                mainContainerRef?.current?.requestFullscreen();
                // @ts-ignore
            } else if (mainContainerRef?.current?.webkitRequestFullscreen) {
                /* Safari */
                // @ts-ignore
                mainContainerRef?.current?.webkitRequestFullscreen();
                // @ts-ignore
            } else if (mainContainerRef?.current?.msRequestFullscreen) {
                /* IE11 */
                // @ts-ignore
                mainContainerRef?.current?.msRequestFullscreen();
            }
        }

        setIsFullScreen(!isFullScreen);
    };

    const handleOpenQueueDialogFromFullScreen = () => {
        if (isFullScreen) {
            handleFullScreen();
        }
    };

    return (
        <Grid container xs={12} justify="center" alignContent="center" alignItems="center" direction="row">
            <DisconnectDialog
                open={openDisconnectModal}
                onLeaveNotFinishing={disconnectEvent}
                onLeaveFinishingEvent={closeEvent}
                onDismiss={() => setOpenDisconnectModal(false)}
            />
            <Grid
                onDoubleClick={handleFullScreen}
                container
                item
                ref={mainContainerRef}
                xs={12}
                direction="row"
                className={clsx(classes.container)}
            >
                <Grid
                    xs={12}
                    container
                    justify="center"
                    alignContent="center"
                    alignItems="center"
                    direction="row"
                >
                    <Grid
                        xs={12}
                        container
                        justify="space-between"
                        alignContent="center"
                        alignItems="center"
                        direction="row"
                        className={clsx(classes.mainVideoContainer)}
                    >
                        <VideoOverlay isFullScreen={isFullScreen} handleFullScreen={handleFullScreen} />
                        <DominantSpeakerOverlay />
                        <div className={classes.userCounter}>
                            <img src={userIcon} alt={t('Viewer counter')} /> {participants.length}
                        </div>
                        {mainParticipant && (
                            <Participant
                                key={mainParticipant.sid}
                                participant={mainParticipant}
                                enableScreenShare
                                isLocalParticipant
                            />
                        )}
                        {!mainParticipant && (
                            <Grid
                                xs={12}
                                container
                                justify="center"
                                alignContent="center"
                                alignItems="center"
                                direction="column"
                                className={clsx(classes.mainVideoPlaceholderContainer)}
                            >
                                <img
                                    className={clsx(classes.mainVideoPlaceholderImage)}
                                    src={mainLogoDark}
                                    alt={t('Gotmy logo')}
                                />
                                <Loader
                                    type="MutatingDots"
                                    color={Colors.main.primary}
                                    secondaryColor={Colors.main.primary}
                                    height={100}
                                    width={100}
                                />
                                <Typography>{t('Loading...')}</Typography>
                            </Grid>
                        )}
                        <RaiseHandQueue onDisplayModal={handleOpenQueueDialogFromFullScreen} />
                    </Grid>
                </Grid>

                <Grid
                    xs={1}
                    container
                    justify="flex-start"
                    alignContent="center"
                    alignItems="center"
                    direction="row"
                    className={classes.audioPlayer}
                >
                    {participants
                        .filter(participant => participant.identity !== influencerUsername)
                        .map(participant => {
                            return (
                                <Participant key={participant.sid + Date.now()} participant={participant} />
                            );
                        })}
                </Grid>
                <Grid
                    justify="flex-start"
                    alignContent="center"
                    alignItems="center"
                    direction="row"
                    className={clsx(
                        classes.remoteVideo,
                        isFullScreen ? classes.remoteVideoFullScreenDimensions : '',
                    )}
                >
                    {remoteParticipant && (
                        <Participant
                            key={remoteParticipant.sid}
                            participant={remoteParticipant}
                            isLocalParticipant={remoteParticipant.identity === localUserUsername}
                        />
                    )}
                </Grid>
            </Grid>
        </Grid>
    );
};

export default DesktopVideoLayout;
